// Los modos que el usuario abrió últimamente —
// ADR-2026-09-19-la-sidebar-muestra-los-dos-modos-mas-usados.
//
// La sidebar de desktop mostraba tres modos por familia elegidos A MANO
// (`destacados` en `GRUPOS_HERRAMIENTAS`, ADR-2026-08-31-los-destacados-de-la-sidebar-van-elegidos-a-mano)
// y ahora muestra los DOS últimos que ese usuario abrió. Este módulo es el registro:
// una lista de ids de herramienta ordenada del último uso al más viejo.
//
// **Es por dispositivo, no por cuenta**: vive en `localStorage`. Entrar desde otra
// computadora arranca sin recientes y los dos grupos muestran sólo su "Generar…", que
// es el estado vacío previsto. Mudarlo a Supabase es otra tarea.
//
// Lo que se anota es ABRIR el modo, no generar en él: el registro lo hace el shell
// mirando el `pathname` (un solo punto, ver `herramientaDeRuta`), así que cuenta
// igual si se llegó desde la sidebar, desde `/modos`, desde la home o por un link.

/** Cuántos usos se recuerdan. De acá salen los 2 por familia que muestra la sidebar. */
export const TOPE_RECIENTES = 12;

const CLAVE = 'atlas-modos-recientes';

/**
 * El evento con el que la sidebar se entera de un uso nuevo sin remontarse.
 *
 * `localStorage` no avisa nada en la MISMA pestaña (el evento `storage` del navegador
 * es sólo para las otras), y la sidebar vive montada mientras el usuario navega de un
 * modo a otro: sin esto, la lista quedaría congelada hasta el próximo refresh.
 */
export const EVENTO_MODO_USADO = 'atlas:modo-usado';

/**
 * La lista con `id` al frente: sin duplicarlo si ya estaba (se MUEVE) y cortada al tope.
 *
 * Pura y exportada para poder testear el orden sin storage de por medio.
 */
export function agregarReciente(
  lista: string[],
  id: string,
  tope = TOPE_RECIENTES,
): string[] {
  return [id, ...lista.filter((x) => x !== id)].slice(0, tope);
}

/**
 * Qué herramienta es esta ruta, o `null` si no es ninguna.
 *
 * Matchea la ruta exacta y sus sub-rutas (`/editor-video/abc-123`), pero SÓLO en el
 * corte de segmento: con un `startsWith` crudo, `/interfaz-v2/plantillas-belleza`
 * también matchearía `/interfaz-v2/plantillas` y el usuario de Belleza terminaría con
 * el modo de Moda —que su rubro ni ve— anotado como reciente.
 *
 * Con varias candidatas se queda con la ruta MÁS LARGA, que es la más específica.
 */
export function herramientaDeRuta(
  pathname: string,
  herramientas: readonly { id: string; ruta: string }[],
): string | null {
  const candidatas = herramientas.filter(
    (h) => pathname === h.ruta || pathname.startsWith(`${h.ruta}/`),
  );
  if (candidatas.length === 0) return null;
  return candidatas.reduce((a, b) => (b.ruta.length > a.ruta.length ? b : a)).id;
}

/**
 * Las `cupo` herramientas más recientes de esta lista, **en el orden en que vienen**.
 *
 * `herramientas` llega ya filtrada por familia, rubro y plan, y en el orden del
 * catálogo. El uso decide CUÁLES entran; el orden de la pantalla sigue siendo el del
 * catálogo, para que tocar el ítem de abajo no lo haga saltar al primer lugar debajo
 * del cursor que lo acaba de tocar.
 *
 * Un id reciente que ya no está en la lista (un modo que el rubro o el plan esconden,
 * o uno que se eliminó del catálogo) simplemente no cuenta.
 */
export function recientesDeFamilia<T extends { id: string }>(
  herramientas: readonly T[],
  recientes: readonly string[],
  cupo: number,
): T[] {
  const elegidas = new Set(
    recientes.filter((id) => herramientas.some((h) => h.id === id)).slice(0, cupo),
  );
  return herramientas.filter((h) => elegidas.has(h.id));
}

/** La lista guardada, del último uso al más viejo. Vacía si no hay nada o está rota. */
export function leerModosRecientes(): string[] {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (!crudo) return [];
    const parseado: unknown = JSON.parse(crudo);
    // El storage lo puede haber escrito una versión vieja o el propio usuario: sin
    // esta guarda, un valor con otra forma tira la sidebar entera al renderizar.
    if (!Array.isArray(parseado)) return [];
    return parseado.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

/** Anota que se abrió esta herramienta y avisa a quien esté mostrando la lista. */
export function registrarModoUsado(id: string): void {
  try {
    const siguiente = agregarReciente(leerModosRecientes(), id);
    localStorage.setItem(CLAVE, JSON.stringify(siguiente));
    // En los tests (entorno `node`) no hay `window` y el registro igual tiene que
    // funcionar: lo que se testea es la lista, no el aviso.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENTO_MODO_USADO));
    }
  } catch {
    // Modo incógnito o storage lleno: la sidebar muestra su estado vacío y listo.
  }
}
